import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { STORAGE_KEYS } from '@/constants/storage';

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

let isRefreshing = false;
let failedQueue: Array<{ resolve: (token: string) => void; reject: (err: Error) => void }> = [];

const processQueue = (err: Error | null, token: string | null = null) => {
  failedQueue.forEach((p) => (err ? p.reject(err) : p.resolve(token!)));
  failedQueue = [];
};

const redirectToLogin = () => {
  sessionStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  window.location.href = `/login?redirect=${encodeURIComponent(window.location.pathname)}`;
};

apiClient.interceptors.request.use((config) => {
  const token = sessionStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

apiClient.interceptors.response.use(null, async (error: AxiosError) => {
  const req = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
  if (error.response?.status !== 401 || req._retry) return Promise.reject(error);
  req._retry = true;

  if (!sessionStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN)) {
    redirectToLogin();
    return Promise.reject(error);
  }

  if (isRefreshing) {
    return new Promise<string>((resolve, reject) => failedQueue.push({ resolve, reject })).then(
      (token) => { req.headers.Authorization = `Bearer ${token}`; return apiClient(req); }
    );
  }

  isRefreshing = true;
  try {
    const { data } = await axios.post<{ accessToken: string }>(
      `${apiClient.defaults.baseURL}/auth/refresh`,
      {},
      { withCredentials: true }
    );
    sessionStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, data.accessToken);
    processQueue(null, data.accessToken);
    req.headers.Authorization = `Bearer ${data.accessToken}`;
    return apiClient(req);
  } catch {
    processQueue(new Error('Unauthorized'), null);
    redirectToLogin();
    return Promise.reject(error);
  } finally {
    isRefreshing = false;
  }
});

export default apiClient;
